import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Coins, Clock, DollarSign, ShoppingCart, CheckCircle, AlertTriangle } from 'lucide-react';
import { Gems } from '@/components/Gems';
import { getGameCoins, calculateGameCoins } from '@/services/pointsService';
import { useAuth } from '@/hooks/useAuth';
import { getPlayerStats } from '@/services/pointsService';
import { purchaseGameWithCoins, hasPlayerPurchasedGame } from '@/services/purchasedKeysService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getDetailedSteamInfo, validateSteamAccount, SteamValidationResult } from '@/services/steamService';
import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';

interface GameCoinsDisplayProps {
  gameId: string;
  gameTitle: string;
  price?: number;
  availableKeys?: number;
  onPurchaseSuccess?: () => void;
}

export function GameCoinsDisplay({
  gameId,
  gameTitle,
  price = 0,
  availableKeys = 0,
  onPurchaseSuccess
}: GameCoinsDisplayProps) {
  const { user } = useAuth();
  const [gameCoins, setGameCoins] = useState<number>(0);
  const [playerStats, setPlayerStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isPurchased, setIsPurchased] = useState<boolean>(false);
  const [purchasing, setPurchasing] = useState<boolean>(false);
  const [actualPublicKeys, setActualPublicKeys] = useState<number>(availableKeys);
  const [allowRequests, setAllowRequests] = useState<boolean>(true);
  const [isSteamConnected, setIsSteamConnected] = useState<boolean>(false);
  const [steamValidation, setSteamValidation] = useState<SteamValidationResult | null>(null);
  const [isValidatingSteam, setIsValidatingSteam] = useState<boolean>(false);

  useEffect(() => {
    loadData();
  }, [gameId, user?.id, availableKeys]);

  const loadData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Compter directement les clés publiques disponibles
      const { count: publicKeysCount, error: countError } = await supabase
        .from("keys")
        .select("*", { count: 'exact', head: true })
        .eq("game_id", gameId)
        .eq("is_claimed", false)
        .eq("public", true);

      if (countError) {
        console.error('GameCoinsDisplay - Error counting public keys:', countError);
      }

      const actualCount = publicKeysCount || 0;
      setActualPublicKeys(actualCount);

      const { data: gameData } = await supabase
        .from("games")
        .select("allow_requests")
        .eq("id", gameId)
        .single();

      if (gameData) {
        setAllowRequests(gameData.allow_requests !== false); // Default to true if null
      }

      const { data: profileData } = await supabase
        .from("profiles")
        .select("steam_id")
        .eq("id", user.id)
        .maybeSingle();

      if (profileData?.steam_id) {
        setIsSteamConnected(true);
        setIsValidatingSteam(true);

        // Fetch detailed Steam info and validate
        const validation = await validateSteamAccount(profileData.steam_id);
        setSteamValidation(validation);
        setIsValidatingSteam(false);

        if (validation.isValid) {
          console.log("Steam account validated successfully for", profileData.steam_id);
        } else {
          console.log("Steam account requirements not met:", validation.errors);
        }

        // Keep the detailed info fetch for the more specific logs if needed, but validation already does much of it
        getDetailedSteamInfo(profileData.steam_id).then(info => {
          if (info) {
            console.log("--- Steam Account Verification Details ---");
            console.log(`Steam ID: ${profileData.steam_id}`);
            console.log(`Persona Name: ${info.summary?.personaname}`);
            console.log(`Steam Level: ${info.level}`);
            console.log(`Games Owned: ${info.gameCount}`);
            console.log(`Total Playtime: ${info.totalPlaytimeHours} hours`);
            console.log(`Profile Visibility: ${info.isPublic ? "Public" : "Private/Friends Only"}`);
            console.log(`Potential Limited Account: ${!info.isPublic || !info.timeCreated ? "Yes (or Private)" : "No (likely)"}`);
            console.log("----------------------------------");
          }
        }).catch(err => console.warn("Could not fetch detailed Steam logs:", err));
      } else {
        setIsSteamConnected(false);
        setSteamValidation(null);
      }

      const [coinsData, statsData, purchased] = await Promise.all([
        getGameCoins(gameId),
        getPlayerStats(user.id),
        hasPlayerPurchasedGame(gameId, user.id)
      ]);

      setGameCoins(coinsData);
      setPlayerStats(statsData);
      setIsPurchased(purchased);
    } catch (error) {
      console.error('Error loading game coins data:', error);
    } finally {
      setLoading(false);
    }
  };

  const canAfford = () => {
    if (!playerStats) return false;
    return playerStats.coins >= gameCoins;
  };

  const handlePurchase = async () => {
    const keysToCheck = actualPublicKeys > 0 ? actualPublicKeys : availableKeys;
    if (!user || !canAfford() || keysToCheck <= 0) return;

    setPurchasing(true);
    try {
      const result = await purchaseGameWithCoins(gameId, user.id, gameCoins);

      if (result.success) {
        setIsPurchased(true);
        const successMessage = result.keyCode
          ? `Successfully purchased ${gameTitle} for ${gameCoins} coins! Your key: ${result.keyCode}`
          : `Successfully purchased ${gameTitle} for ${gameCoins} coins!`;
        toast.success(successMessage);

        // Reload player stats to update coins
        const statsData = await getPlayerStats(user.id);
        setPlayerStats(statsData);

        // Call success callback
        if (onPurchaseSuccess) {
          onPurchaseSuccess();
        }
      } else {
        toast.error(result.error || 'Failed to purchase game');
      }
    } catch (error) {
      console.error('Error purchasing game:', error);
      toast.error('An error occurred while purchasing the game');
    } finally {
      setPurchasing(false);
    }
  };

  if (loading || isValidatingSteam) {
    return (
      <Card className="p-3">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="h-4 bg-muted rounded w-20 animate-pulse"></div>
            <div className="flex items-center gap-1">
              <div className="h-4 w-4 bg-muted rounded animate-pulse"></div>
              <div className="h-6 bg-muted rounded w-12 animate-pulse"></div>
            </div>
          </div>
          <div className="h-8 bg-muted rounded w-full animate-pulse"></div>
        </div>
      </Card>
    );
  }

  const hasEnoughCoins = canAfford();
  const keysToCheck = actualPublicKeys > 0 ? actualPublicKeys : availableKeys;
  const isAvailable = allowRequests && keysToCheck > 0;

  // If game is already purchased, show success message
  if (isPurchased) {
    return (
      <Card className="p-3">
        <div className="flex items-center gap-2 text-green-600">
          <CheckCircle className="w-4 h-4" />
          <span className="text-sm font-medium">Game already received</span>
        </div>
      </Card>
    );
  }


  return (
    <Card className="p-3">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium mr">Gems Cost</h3>
          <Badge variant="outline" className="flex items-center gap-1 text-xs">
            {gameCoins} <Gems with_text={false} size={12} />
          </Badge>
        </div>

        {!isSteamConnected ? (
          <p className="text-xs text-orange-500 font-medium flex items-center gap-1 justify-center py-1">
            <Clock className="w-3 h-3" />
            Steam account required
          </p>
        ) : steamValidation && !steamValidation.isValid ? (
          <div className="space-y-2">
            <p className="text-xs text-red-500 font-medium flex items-center gap-1 justify-center py-1">
              <AlertTriangle className="w-3 h-3" />
              Account requirements not met
            </p>
            <Button variant="outline" size="sm" className="w-full h-7 text-[10px]" asChild>
              <Link to="/help#steam-requirements">
                <Info className="w-3 h-3 mr-1" />
                See Help
              </Link>
            </Button>
          </div>
        ) : !isAvailable ? (
          <p className="text-xs text-orange-500 font-medium flex items-center gap-1 justify-center py-1">
            <Clock className="w-3 h-3" />
            Not available
          </p>
        ) : hasEnoughCoins ? (
          <Button
            size="sm"
            onClick={handlePurchase}
            disabled={purchasing}
            className="w-full h-8 text-xs"
          >
            <ShoppingCart className="w-3 h-3 mr-1" />
            {purchasing ? 'Purchasing...' : <span className="flex items-center gap-1">Get with <Gems with_text={true} size={10} /></span>}
          </Button>
        ) : (
          <p className="text-xs text-red-600 flex items-center gap-1">
            Missing {gameCoins - (playerStats?.coins || 0)} <Gems with_text={false} size={10} />
          </p>
        )}
      </div>
    </Card>
  );
}
